import { useMutation } from '@tanstack/react-query';

import { defaultApiConfig } from './defaultApiConfig';

/**
 * Upload of the Schwab Advisor Center "RMD All Ages" export.
 *
 * Calls portfolio-optimizer `POST /custodian-rmd/schwab-export` with the raw file as the request body
 * (text/csv). Written against fetch rather than the generated client because the OpenAPI spec is pulled
 * from the deployed service (`yarn openapi-gen`); switch to the generated `CustodianRmdApi` once it exists.
 */

export interface SchwabRmdExportUploadResult {
    fileName: string;
    /** yyyy-MM-dd, the S3 key date the file was stored under: T-1, the previous NYSE business day */
    tradeDate: string;
    recordCount: number;
    s3Key: string;
}

export interface SchwabRmdExportUploadRequest {
    file: File;
}

const UPLOAD_PATH = '/portfolio-optimizer/custodian-rmd/schwab-export';

export const uploadSchwabRmdExport = async ({ file }: SchwabRmdExportUploadRequest) => {
    const query = new URLSearchParams();
    query.set('file_name', file.name);

    const response = await fetch(`${defaultApiConfig.basePath}${UPLOAD_PATH}?${query.toString()}`, {
        method: 'POST',
        credentials: defaultApiConfig.credentials,
        headers: { 'Content-Type': 'text/csv' },
        body: file,
    });

    const body = await response.json().catch(() => undefined);
    if (!response.ok) {
        // FastAPI puts validation messages in `detail`, the generic handler in `message`
        const message =
            (typeof body?.detail === 'string' && body.detail) ||
            (typeof body?.message === 'string' && body.message) ||
            `Upload failed (${response.status})`;
        throw new Error(message);
    }

    return {
        fileName: body.file_name,
        tradeDate: body.trade_date,
        recordCount: body.record_count,
        s3Key: body.s3_key,
    } as SchwabRmdExportUploadResult;
};

export const useUploadSchwabRmdExport = () =>
    useMutation({
        mutationFn: uploadSchwabRmdExport,
    });
